import { createContext, useContext, useReducer, useMemo } from 'react';
import moment from 'moment';

const ZoneContext = createContext(null);

const ZoneDispatchContext = createContext(null);

const createInitialState = () => ({

    // Campos básicos del formulario
    general: {
        name: '',
        type: 'holiday',
        requiredApproval: false,
        description: ''
    },

    // Estados específicos por tipo de zona
    holiday: {
        holidays: [],
        selectedHolidays: [],
        definedBy: 'hours_in_the_holiday',
        deductedTime: 'none',
        hoursWorkedBelongTo: 'fixed_rule_value'
    },

    weekDays: {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false,
        zoneApplies: 'hours_worked'
    },

    consecutiveDays: {
        overtimeSustitutions: [],
        selectedOvertimeSustitution: [],
        dayAmount: 0,
        minHours: moment(new Date().setHours(0, 0, 0, 0)),
        minHoursLastDay: moment(new Date().setHours(0, 0, 0, 0)),
        typeConsecutive: 'consecute_days',
        resetTo: 'day_number',
        dayNumber: 0,
        weekday1: 'monday',
        weekday2: 'monday'
    },

    diferentialDaily: {
        start: moment(new Date().setHours(12, 0, 0, 0)),
        end: moment(new Date().setHours(12, 0, 0, 0)),
        withinZone: moment(new Date().setHours(0, 0, 0, 0)),
        withinTurn: moment(new Date().setHours(0, 0, 0, 0)),
        early: moment(new Date().setHours(0, 0, 0, 0)),
        late: moment(new Date().setHours(0, 0, 0, 0)),
        basedBy: 'real_punch',
        planned: false,
        beforeStart: false,
        afterStart: false,
        afterTimeStart: moment(new Date().setHours(12, 0, 0, 0)),
        beforeTimeStart: moment(new Date().setHours(12, 0, 0, 0)),
        beforeFinish: false,
        afterFinish: false,
        afterTimeFinish: moment(new Date().setHours(12, 0, 0, 0)),
        beforeTimeFinish: moment(new Date().setHours(12, 0, 0, 0))
    },

    diferentialWeekly: {
        startWeekly: moment(new Date().setHours(12, 0, 0, 0)),
        endWeekly: moment(new Date().setHours(12, 0, 0, 0)),
        withinZoneWeekly: moment(new Date().setHours(0, 0, 0, 0)),
        withinTurnWeekly: moment(new Date().setHours(0, 0, 0, 0)),
        earlyWeekly: moment(new Date().setHours(0, 0, 0, 0)),
        lateWeekly: moment(new Date().setHours(0, 0, 0, 0)),
        basedByWeekly: 'real_punch',
        planned: false,
        beforeStartWeekly: false,
        afterStartWeekly: false,
        afterTimeStartWeekly: moment(new Date().setHours(12, 0, 0, 0)),
        beforeTimeStartWeekly: moment(new Date().setHours(12, 0, 0, 0)),
        beforeFinishWeekly: false,
        afterFinishWeekly: false,
        afterTimeFinishWeekly: moment(new Date().setHours(12, 0, 0, 0)),
        beforeTimeFinishWeekly: moment(new Date().setHours(12, 0, 0, 0)),
        weekday1Weekly: 'monday',
        weekday2Weekly: 'monday'
    }
});

const zoneReducer = (zone, action) => {
    switch (action.type) {
        case 'UPDATE_FIELD':
            return {
                ...zone,
                [action.section]: {
                    ...zone[action.section],
                    [action.field]: action.value
                }
            };

        case 'UPDATE_SECTION':
            return {
                ...zone,
                [action.section]: {
                    ...zone[action.section],
                    ...action.data
                }
            };

        case 'RESET_SECTION':
            return {
                ...zone,
                [action.section]: createInitialState()[action.section]
            };

        case 'RESET_FORM':
            return createInitialState();

        case 'CHANGE_TYPE':
            const newState = {
                ...zone,
                general: {
                    ...zone.general,
                    type: action.value
                }
            };

            // Resetear todas las secciones específicas cuando cambia el tipo
            const initialState = createInitialState();
            return {
                ...newState,
                holiday: initialState.holiday,
                weekDays: initialState.weekDays,
                consecutiveDays: initialState.consecutiveDays,
                diferentialDaily: initialState.diferentialDaily,
                diferentialWeekly: initialState.diferentialWeekly
            };

        default:
            console.warn(`Acción no reconocida: ${action.type}`);
            return zone;
    }
};

export const ZoneProvider = ({ children }) => {
    const [zone, dispatch] = useReducer(zoneReducer, createInitialState());

    return (
        <ZoneContext.Provider value={zone}>
            <ZoneDispatchContext.Provider value={dispatch}>
                {children}
            </ZoneDispatchContext.Provider>
        </ZoneContext.Provider>
    );
};

export const useZone = () => {
    return useContext(ZoneContext);
};

const useZoneDispatch = () => {
    return useContext(ZoneDispatchContext);
};

export const useZoneActions = () => {
    const dispatch = useZoneDispatch();

    return useMemo(() => ({
        updateField: (section, field, value) => {
            dispatch({ type: 'UPDATE_FIELD', section, field, value });
        },
        updateSection: (section, data) => {
            dispatch({ type: 'UPDATE_SECTION', section, data });
        },
        resetSection: (section) => {
            dispatch({ type: 'RESET_SECTION', section });
        },
        resetForm: () => {
            dispatch({ type: 'RESET_FORM' });
        },
        changeType: (value) => {
            dispatch({ type: 'CHANGE_TYPE', value });
        }
    }), [dispatch]);
};

;